import { defineStore } from "pinia";

import { IBlade } from "../models/blade"
import { IBladeFilter } from "../models/filters/bladeFilter"

import BladeService from "../services/BladeService";
import { IBladeForm } from "../models/forms/bladeForm";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { IParameter } from "../models/parameter";
import { ICenter } from "../models/center";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

export const useBladeStore = defineStore("blades", {
    state: () => ({
        datas: [] as Array<IBlade>,
        current : {} as IBlade,
        itemForm : {} as IBladeForm,
        isLoading: false as boolean,
    }),

    getters: {

        isCodeDisponible : (state) => (batchNumber: string, id:number) => {

            var stateFilter = state.datas.filter((item) => item.batchNumber == batchNumber && id == item.center?.id)

            return {
                isExisting : stateFilter.length == 0,
                id : stateFilter.length == 0 ? 0 : stateFilter[0].id
            };
        },

        getBladeArchived : (state) => (isBladeArchived : boolean) => {
            return state.datas.filter(x => x.isArchived == false || x.isArchived == isBladeArchived);
        },

    },

    actions: {

        /**
         * Chargement des données dans le store
         * @param filter Le filtre à appliquer pour le chargement des données
         * @returns 
         */
        async load(filter: IBladeFilter) {
            this.isLoading = true;
            let result = await BladeService.getAll<IBladeFilter,IBlade>(filter);
            this.isLoading = false;
            this.datas = [];
            if (!result.error) {
                var resultData = result.data as IBlade[];
                this.datas = resultData;
            }
            return result;
        },

        /**
         * Récupération d'une lame par son identifiant
         * @param id 
         * @returns 
         */
        async get(id: number) {
            let result = await BladeService.getById<IBlade>(id);
            // mettre à jour dans le store ?
            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == id);
                if (index >= 0)
                    this.datas[index] = result.data as IBlade;
                // on le stock dans le current puis dans le form
                this.current = result.data as IBlade;
                this.itemForm = this.toForm(result.data as IBlade);
            }
            return result;
        },

        /**
        * Récupération de l'objet dans le store
        * @param id Identifiant de l'objet
        * @returns 
        */
        async searchStoreById (id: number) {
            this.current = this.datas.find((objet) => objet.id == id)!;
            this.itemForm = await this.toForm(this.current as IBlade);
            return this.current;
        },

        /**
         * Mise à jour ou création d'un objet
         * @param maj 
         * @returns 
         */
        async save(maj:boolean = true, isPatch:boolean = false)
        {   
            let result = {} as ServiceOperationResult<IBlade|IDataOperationResult<IBlade>>;  

            console.log("Sauvegarde lame : ", this.itemForm);

            console.log(this.itemForm);

            if ( this.itemForm.id > 0) {
                result = await BladeService.update(this.itemForm);
                
                if ( !result.error)
                {
                    this.current = result.data as IBlade;
                    if ( maj ) {
                        let index = this.datas.findIndex((a) => a.id == this.itemForm.id);
                        if (index >= 0) {
                            this.datas[index] = this.current;
                        } else {
                            const error = new DataOperationError();
                            error.REQUEST_ERROR = "Item non trouvé dans le store";
                            return new ServiceOperationResult<IDataOperationResult<IBlade>>(true, {errors: error});
                        }    
                    }
                }
            } else {
                result = await BladeService.create(this.itemForm);
                if ( !result.error)
                {
                    this.current = result.data as IBlade;
                    if (this.datas.findIndex((a) => a.id == this.current?.id) == -1) {
                        this.datas.push(this.current);
                    }
                }
                
            }
            return result;
        },

        /**
         * Delete de l'objet
         * @returns 
         */
        async delete() {
            let result = await BladeService.delete<IBlade>(this.current.id);

            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == this.current.id);
                if (index >= 0) {
                    // On remove l'élément des données
                    this.datas.splice(index, 1);
                }                  

                this.current = this.newItem(undefined);
                this.itemForm = this.newForm(0);
            }
            return result;            
        },

        /**
         * Transformation de l'objet en formulaire
         * @param item Blade
         * @returns 
         */
        toForm(item: IBlade): IBladeForm {
            const form = {} as IBladeForm;
            form.id = item.id;

            form.batchNumber = item.batchNumber;
            form.isArchived = item.isArchived ?? false;
            form.expirationDate = item.expirationDate ?? undefined;
            form.bladeProviderId = item.bladeProvider ? item.bladeProvider.id : 0;
            form.bladeTypeId = item.bladeType ? item.bladeType.id : 0;
            form.centerId = item.center ? item.center.id : 0;

            this.itemForm = form;
            return form;
        },

        /**
         * Permet d'initialiser un objet vide pour le formulaire
         * @returns 
         */
        newForm(centerId : number): IBladeForm {
            this.itemForm = {
                id: 0,
                batchNumber: "",
                isArchived: false,
                bladeProviderId: 0,
                bladeTypeId: 0,
                centerId: centerId,
            } as IBladeForm;
            return this.itemForm;
        },
        
        /**
         *  Permet d'initialiser un objet vide pour les referentiels
         * @returns 
         */
        newItem(center? : ICenter): IBlade {

            this.current = {
                id: 0,
                batchNumber: "",
                isArchived: false,
                bladeProvider: { id: 0 } as IParameter,
                bladeType: { id: 0 } as IParameter,
                center: center
            } as IBlade;

            this.itemForm = this.toForm(this.current);
            return this.current;
        },
    }
})

signalRManager.connection.on("MajBlade", (payload: IPayloadStore) => {
    const store = useBladeStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                store.datas.push(payload.data as IBlade);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                store.datas.splice(idx, 1, payload.data as IBlade);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});